import { useEffect, useMemo, useState } from 'react';
import { Button, Cluster, Field, Input, Panel, Select, StatusBadge, Textarea } from '../../components';
import type { AmoyeApi, LocalInvoiceSubmission, ReviewedInvoiceFactsInput } from '../../lib/api';
import { buildReviewedInvoiceFacts, calculateInvoiceTotals, formatKobo, initialBusinessInvoiceForm,
  type BusinessInvoiceFormValues, type InvoicePartyFormValues, type InvoiceResourceView,
  type MockInvoiceReceiptView } from './invoice';

export interface BusinessInvoiceInteractionProps {
  businessName: string;
  invoice?: InvoiceResourceView;
  receipt?: MockInvoiceReceiptView;
  busy?: boolean;
  localSubmission?: LocalInvoiceSubmission;
  localConfirmationRequired?: boolean;
  onRefresh?(): Promise<void>;
  onRetry?(): Promise<void>;
  onCreate(facts: ReviewedInvoiceFactsInput): Promise<void>;
  onAction(action: 'prepare' | 'review' | 'approve' | 'mock-dispatch'): Promise<void>;
}

export function createOwnBusinessInvoiceDraft(api: Pick<AmoyeApi, 'createInvoiceFromReviewedFacts'>,
  taxpayerId: string, facts: ReviewedInvoiceFactsInput) {
  return api.createInvoiceFromReviewedFacts({ track: 'B1', taxpayerId, facts });
}

export function mutateOwnBusinessInvoiceDraft(api: Pick<AmoyeApi, 'mutateInvoice'>, id: string,
  action: 'prepare' | 'review' | 'approve') {
  const capacityId = action === 'prepare' ? 'business-owner-preparer'
    : action === 'review' ? 'business-owner-reviewer' : 'business-owner-approver';
  return api.mutateInvoice(id, action, capacityId);
}

export function requestOwnBusinessInvoiceMock(api: Pick<AmoyeApi, 'dispatchInvoiceMock'>, id: string) {
  return api.dispatchInvoiceMock(id, { capacityId: 'business-owner-mock-requester',
    idempotencyKey: crypto.randomUUID(), behavior: 'receive' });
}

export function OwnBusinessInvoiceInteraction(props: BusinessInvoiceInteractionProps) {
  const [open, setOpen] = useState(false);
  return !open && !props.invoice ? <Panel title="Own-business e-invoice draft"
    description="Open the reviewed-fact form when you are ready to prepare an invoice for your own business.">
    <Button onClick={() => setOpen(true)} tone="secondary">Create own-business e-invoice</Button>
  </Panel> : <BusinessInvoiceInteraction {...props} />;
}

export function BusinessInvoiceInteraction({ businessName, invoice, receipt, busy = false, onCreate,
  onAction, localSubmission, localConfirmationRequired = false, onRefresh, onRetry }: BusinessInvoiceInteractionProps) {
  const [values, setValues] = useState(() => initialBusinessInvoiceForm(businessName));
  const [error, setError] = useState<string>();
  const [dirty, setDirty] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => setConfirmed(false), [invoice?.id, invoice?.version, invoice?.payloadSha256]);
  const savedDetails = useMemo(() => invoice?.payloadBase64 ? readSavedInvoice(invoice.payloadBase64) : undefined,
    [invoice?.payloadBase64]);
  const preview = useMemo(() => {
    try { return values.unitPriceNaira ? calculateInvoiceTotals(values) : undefined; } catch { return undefined; }
  }, [values]);
  const update = <K extends keyof BusinessInvoiceFormValues>(key: K, value: BusinessInvoiceFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));
  return <Panel className="am-b1-invoice-builder" title={invoice ? `Invoice ${invoice.invoiceNumber}` : 'Create a reviewed e-invoice draft'}
    description="Review the invoice details before approval and delivery.">
    {!invoice && <form data-amoye-unsaved={dirty ? 'true' : 'false'} onChange={() => setDirty(true)} onSubmit={async (event) => {
      event.preventDefault(); setError(undefined);
      try {
        const facts = await buildReviewedInvoiceFacts(values, new Date().toISOString());
        await onCreate(facts);
        setDirty(false);
      } catch (caught) { setError(caught instanceof Error ? caught.message : 'The invoice draft could not be created.'); }
    }}>
      <div className="am-b1-party-grid">
        <InvoicePartyFields label="Seller" value={values.seller} onChange={(seller) => update('seller', seller)} />
        <InvoicePartyFields label="Buyer" value={values.buyer} onChange={(buyer) => update('buyer', buyer)} />
      </div>
      <fieldset className="am-b1-fieldset"><legend>Invoice and supply</legend><div className="am-b1-form-grid">
        <Field label="Invoice number" required><Input required value={values.invoiceNumber}
          onChange={(event) => update('invoiceNumber', event.target.value)} /></Field>
        <Field label="Buyer reference" required><Input required value={values.buyerReference}
          onChange={(event) => update('buyerReference', event.target.value)} /></Field>
        <Field label="Issue date" required><Input required type="date" value={values.issueDate}
          onChange={(event) => update('issueDate', event.target.value)} /></Field>
        <Field label="Due date" required><Input required type="date" value={values.dueDate}
          onChange={(event) => update('dueDate', event.target.value)} /></Field>
        <Field label="Payment means code" hint="Use the reviewed Peppol payment-means code." required><Input inputMode="numeric" required
          value={values.paymentMeansCode} onChange={(event) => update('paymentMeansCode', event.target.value)} /></Field>
        <Field className="am-b1-wide" label="Supply description" required><Textarea required value={values.description}
          onChange={(event) => update('description', event.target.value)} /></Field>
        <Field label="Quantity" hint="Whole units in this release." required><Input inputMode="numeric" min="1" pattern="[1-9][0-9]*" required
          value={values.quantity} onChange={(event) => update('quantity', event.target.value)} /></Field>
        <Field label="Unit code" required><Input required value={values.unitCode}
          onChange={(event) => update('unitCode', event.target.value.toUpperCase())} /></Field>
        <Field label="Unit price (naira)" hint="No commas; at most two decimal places." required><Input inputMode="decimal"
          pattern="(0|[1-9][0-9]*)([.][0-9]{1,2})?" required value={values.unitPriceNaira}
          onChange={(event) => update('unitPriceNaira', event.target.value)} /></Field>
        <Field label="VAT category" required><Select required value={values.vatCategory}
          onChange={(event) => { const category = event.target.value as BusinessInvoiceFormValues['vatCategory'];
            setValues((current) => ({ ...current, vatCategory: category,
              vatRatePercent: category === 'S' ? current.vatRatePercent || '7.5' : '0' })); }}>
          <option value="S">Standard rated</option><option value="Z">Zero rated</option><option value="E">Exempt</option>
        </Select></Field>
        <Field label="VAT rate (%)" hint="Confirm the applicable rate. Amoye does not ask AI to choose a tax rate." required><Input inputMode="decimal"
          pattern="(0|[1-9][0-9]?|100)([.][0-9]{1,2})?" required value={values.vatRatePercent}
          onChange={(event) => update('vatRatePercent', event.target.value)} /></Field>
        {values.vatCategory === 'E' && <Field className="am-b1-wide" label="Tax exemption reason" required><Textarea required
          value={values.taxExemptionReason} onChange={(event) => update('taxExemptionReason', event.target.value)} /></Field>}
      </div></fieldset>
      {preview && <dl aria-label="Invoice totals" className="am-b1-totals">
        <div><dt>Tax-exclusive</dt><dd>{formatKobo(preview.lineExtensionAmountKobo)}</dd></div>
        <div><dt>VAT</dt><dd>{formatKobo(preview.taxAmountKobo)}</dd></div>
        <div><dt>Payable</dt><dd>{formatKobo(preview.payableAmountKobo)}</dd></div>
      </dl>}
      <label className="am-b1-review-confirmation"><input required type="checkbox" />
        <span>I have reviewed the buyer, seller, supply, price and VAT facts shown above.</span></label>
      {error && <p className="am-b1-invoice-error" role="alert">{error}</p>}
      <Button disabled={Boolean(invoice)} loading={busy} type="submit">Create reviewed invoice draft</Button>
    </form>}
    {invoice && <section aria-label="Validated invoice" className="am-b1-artifact">
      <dl className="am-b1-totals"><div><dt>Invoice number</dt><dd>{invoice.invoiceNumber}</dd></div>
        <div><dt>Payable</dt><dd>{formatKobo(invoice.payableAmountKobo)}</dd></div>
        {savedDetails && <><div><dt>Seller</dt><dd>{savedDetails.seller}</dd></div>
          <div><dt>Buyer</dt><dd>{savedDetails.buyer}</dd></div></>}
      </dl>
      {savedDetails && <ul aria-label="Saved invoice supplies">{savedDetails.lines.map((line, index) => <li key={index}>{line}</li>)}</ul>}
      <><div><span>Standards check</span><StatusBadge tone={invoice.validationState === 'SCHEMA_AND_RULES_VALIDATED' ? 'success' : 'warning'}>
        {invoice.validationState.replaceAll('_', ' ').toLowerCase()}</StatusBadge></div>
        <details><summary>View technical audit reference</summary><code>{invoice.payloadSha256}</code></details>
        <div><span>Invoice state</span><strong>{invoice.state.replaceAll('_', ' ').toLowerCase()}</strong></div>
        <Cluster><Button disabled={invoice.state !== 'DRAFT' || busy} onClick={() => onAction('prepare')} size="small">Prepare draft</Button>
          <Button disabled={invoice.state !== 'PREPARED' || busy} onClick={() => onAction('review')} size="small">Review draft</Button>
          <Button disabled={invoice.state !== 'REVIEWED' || busy || (localConfirmationRequired && !confirmed)} onClick={() => onAction('approve')} size="small">Approve draft</Button>
          <Button disabled={invoice.state !== 'APPROVED' || busy || (localConfirmationRequired && !confirmed)} onClick={() => onAction('mock-dispatch')} size="small">{localConfirmationRequired ? 'Submit invoice' : 'Test invoice delivery'}</Button></Cluster></>
      {localConfirmationRequired && ['REVIEWED', 'APPROVED'].includes(invoice.state) && <label className="am-b1-review-confirmation">
        <input type="checkbox" checked={confirmed} disabled={busy} onChange={(event) => setConfirmed(event.target.checked)} />
        <span>I confirm the saved invoice {invoice.invoiceNumber}, version {invoice.version}, for {formatKobo(invoice.payableAmountKobo)} is ready for {invoice.state === 'REVIEWED' ? 'approval' : 'submission'}.</span>
      </label>}
      <p>Test environment: delivery responses are simulated. No government clearance, buyer delivery or payment is created.</p>
    </section>}
    {localSubmission && <section aria-live="polite" className="am-b1-mock-receipt">
      <h3>Invoice delivery</h3><p>{localSubmission.message}</p>
      {localSubmission.receiptReference && <p>Reference: {localSubmission.receiptReference}</p>}
      <ol aria-label="Invoice submission activity">{localSubmission.events.map((event, index) => <li key={`${event.state}:${index}`}>
        {event.message} <time dateTime={event.occurredAt}>{new Date(event.occurredAt).toLocaleString()}</time>
      </li>)}</ol>
      {!['ACCEPTED', 'REJECTED'].includes(localSubmission.state) && <Cluster>
        <Button disabled={busy || !onRefresh} size="small" onClick={() => void onRefresh?.()}>Check status</Button>
        {localSubmission.retryable && <Button disabled={busy || !onRetry} size="small" onClick={() => void onRetry?.()}>Retry delivery</Button>}
      </Cluster>}
    </section>}
    {receipt && <section aria-live="polite" className="am-b1-mock-receipt">
      <StatusBadge tone={receipt.state === 'MOCK_RECEIVED' ? 'success' : receipt.state === 'MOCK_REJECTED' ? 'danger' : 'warning'}>{receiptLabel(receipt.state)}</StatusBadge>
      <h3>Test delivery result</h3><p>This confirms only that the local test connection processed the invoice data. It is not proof of delivery to the tax authority or buyer.</p>
      {receipt.validationErrors.length > 0 && <ul aria-label="Mock validation errors">{receipt.validationErrors.map((item) => <li key={item}>{item}</li>)}</ul>}
    </section>}
  </Panel>;
}

function readSavedInvoice(payload: string): { seller: string; buyer: string; lines: string[] } | undefined {
  try {
    const bytes = Uint8Array.from(atob(payload), (character) => character.charCodeAt(0));
    const xml = new DOMParser().parseFromString(new TextDecoder().decode(bytes), 'application/xml');
    if (xml.getElementsByTagName('parsererror').length) return undefined;
    const local = (root: Document | Element, name: string) => root.getElementsByTagNameNS('*', name)[0];
    const supplier = local(xml, 'AccountingSupplierParty');
    const customer = local(xml, 'AccountingCustomerParty');
    return { seller: supplier && local(supplier, 'RegistrationName')?.textContent || 'Review saved supplier details',
      buyer: customer && local(customer, 'RegistrationName')?.textContent || 'Review saved buyer details',
      lines: [...xml.getElementsByTagNameNS('*', 'InvoiceLine')].map((line) => {
        const item = local(line, 'Item');
        return `${item && local(item, 'Name')?.textContent || 'Supply'} · quantity ${local(line, 'InvoicedQuantity')?.textContent ?? ''} · net ₦${local(line, 'LineExtensionAmount')?.textContent ?? ''}`;
      }) };
  } catch { return undefined; }
}

function receiptLabel(state: MockInvoiceReceiptView['state']): string {
  if (state === 'MOCK_RECEIVED') return 'Test received';
  if (state === 'MOCK_REJECTED') return 'Test failed';
  return 'Check required';
}

function InvoicePartyFields({ label, value, onChange }: { label: 'Seller' | 'Buyer'; value: InvoicePartyFormValues;
  onChange(value: InvoicePartyFormValues): void }) {
  const update = (key: keyof InvoicePartyFormValues, next: string) => onChange({ ...value, [key]: next });
  return <fieldset className="am-b1-fieldset"><legend>{label}</legend><div className="am-b1-form-grid">
    <Field className="am-b1-wide" label={`${label} legal name`} required><Input required value={value.registrationName}
      onChange={(event) => update('registrationName', event.target.value)} /></Field>
    <Field label={`${label} identifier scheme`} hint="Use the reviewed Peppol scheme code." required><Input required value={value.identifierScheme}
      onChange={(event) => update('identifierScheme', event.target.value)} /></Field>
    <Field label={`${label} electronic address`} required><Input required value={value.electronicAddress}
      onChange={(event) => update('electronicAddress', event.target.value)} /></Field>
    <Field label={`${label} party identifier`} required><Input required value={value.partyId}
      onChange={(event) => update('partyId', event.target.value)} /></Field>
    <Field label={`${label} company identifier`} required><Input required value={value.companyId}
      onChange={(event) => update('companyId', event.target.value)} /></Field>
    <Field label={`${label} tax registration identifier`} required><Input required value={value.taxRegistrationId}
      onChange={(event) => update('taxRegistrationId', event.target.value)} /></Field>
    <Field className="am-b1-wide" label={`${label} street address`} required><Input required value={value.streetName}
      onChange={(event) => update('streetName', event.target.value)} /></Field>
    <Field label={`${label} city`} required><Input required value={value.cityName}
      onChange={(event) => update('cityName', event.target.value)} /></Field>
    <Field label={`${label} postal code`}><Input value={value.postalZone}
      onChange={(event) => update('postalZone', event.target.value)} /></Field>
    <Field label={`${label} country code`} required><Input maxLength={2} required value={value.countryCode}
      onChange={(event) => update('countryCode', event.target.value.toUpperCase())} /></Field>
  </div></fieldset>;
}
